import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Home,
  LoaderCircle,
  Mail,
  MapPin,
  Search,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./OssRequestsPage.css";
import { useToast } from "../../../components/feedback/ToastProvider";
import { acceptOssRequest, rejectOssRequest } from "./ossRequests";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const EMPTY_ARRAY = [];

async function fetchOssConnectionRequests({
  accessToken,
  requestType,
  status = "PENDING",
  search = "",
  postalCodes = [],
  page = 1,
  pageSize = 3,
}) {
  const params = new URLSearchParams();

  if (requestType) {
    params.set("request_type", requestType);
  }

  if (status && status !== "ALL") {
    params.set("status", status);
  }

  if (search) {
    params.set("search", search);
  }

  postalCodes.forEach((postalCode) => {
    params.append("postal_code", postalCode);
  });

  params.set("page", page);

  params.set("page_size", pageSize);

  const res = await fetch(
    `${API_BASE}/api/oss/connection-requests/?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.detail || "Could not load OSS requests.");
  }

  return data;
}

async function contactOssConnectionRequest({ accessToken, requestId }) {
  const res = await fetch(
    `${API_BASE}/api/connection-requests/${requestId}/contact/`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    },
  );

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.detail || "Could not open the conversation.");
  }

  return data;
}

async function fetchOssCoveragePostalCodes({ accessToken }) {
  const res = await fetch(`${API_BASE}/api/oss/coverage-postal-codes/`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.detail || "Could not load postal codes.");
  }

  return Array.isArray(data?.postal_codes) ? data.postal_codes : [];
}

function formatDisplayLabel(value = "") {
  return String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatAddress(property = {}, t) {
  if (property.full_address) return property.full_address;

  const address = [
    [property.street_name, property.house_number].filter(Boolean).join(" "),
    property.postal_code,
    property.city,
  ]
    .filter(Boolean)
    .join(" · ");

  return address || t("common.notAvailable");
}

function formatMoney(value) {
  if (value == null || value === "") return "—";

  const cleaned = String(value).replace(/[€\s]/g, "");
  const normalized = cleaned.replace(/,/g, "");
  const numeric = Number(normalized);

  if (Number.isNaN(numeric)) {
    return `€${value}`;
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatPreferenceValue(value = "") {
  if (!value) return "—";

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function StatusPill({ status, t }) {
  const normalized = status || "PENDING";

  return (
    <span
      className={`oss-requests-status oss-requests-status--${normalized.toLowerCase()}`}
    >
      {t(`statuses.${normalized}`, formatDisplayLabel(normalized))}
    </span>
  );
}

function StatusTabs({ value, onChange, t }) {
  const tabs = ["PENDING", "ACCEPTED", "DECLINED", "ALL"];

  return (
    <div className="oss-requests-status-tabs" role="tablist">
      {tabs.map((status) => (
        <button
          key={status}
          type="button"
          className={value === status ? "is-active" : ""}
          onClick={() => onChange(status)}
        >
          {t(`filters.${status}`, formatDisplayLabel(status))}
        </button>
      ))}
    </div>
  );
}

function Pagination({ count, page, pageSize, totalPages, onPageChange, t }) {
  if (count <= pageSize) return null;

  return (
    <div className="oss-requests-pagination">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label={t("pagination.previous")}
      >
        <ChevronLeft size={15} />
      </button>

      <span>{t("pagination.pageOf", { page, totalPages })}</span>

      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        aria-label={t("pagination.next")}
      >
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

function RequestCard({
  item,
  withProperty = false,
  t,
  accessToken,
  acceptingId,
  rejectingId,
  onAcceptClick,
  onRejectClick,
}) {
  const homeowner = item.homeowner || {};
  const property = item.property || {};
  const navigate = useNavigate();

  const avatarUrl = homeowner.profile_image_url;
  const initials =
    (homeowner.first_name?.[0] || "") + (homeowner.last_name?.[0] || "");

  const isPending = item.status === "PENDING";
  const isPrePurchase = !withProperty || item.request_type === "PRE_PURCHASE";

  const prePurchase = item.pre_purchase || {};

  const prePurchaseBudget =
    prePurchase.budget || prePurchase.budget_range_text || "";

  const prePurchaseRegion =
    prePurchase.region || prePurchase.preferred_location || "";

  const prePurchaseTimeline = prePurchase.timeline || "";

  const prePurchaseMessage =
    prePurchase.message ||
    item.message_to_oss ||
    t("requests.lookingForPropertyFallback");

  const [tooltip, setTooltip] = React.useState({
    visible: false,
    x: 0,
    y: 0,
  });

  const [contacting, setContacting] = React.useState(false);

  function openRequestDetails() {
    if (isPrePurchase) return;

    navigate(`/oss/requests/${item.id}`, {
      state: { request: item },
    });
  }

  return (
    <article
      className={`oss-request-card ${
        isPrePurchase ? "oss-request-card--static" : ""
      }`}
      role={isPrePurchase ? "article" : "button"}
      tabIndex={isPrePurchase ? undefined : 0}
      onClick={openRequestDetails}
      onMouseMove={(e) => {
        if (isPrePurchase) return;

        if (e.target.closest(".oss-request-card__actions")) {
          setTooltip({ visible: false, x: 0, y: 0 });
          return;
        }

        setTooltip({
          visible: true,
          x: e.clientX,
          y: e.clientY,
        });
      }}
      onMouseLeave={() =>
        setTooltip({
          visible: false,
          x: 0,
          y: 0,
        })
      }
      onKeyDown={(e) => {
        if (!isPrePurchase && e.key === "Enter") {
          openRequestDetails();
        }
      }}
    >
      <div className="oss-request-card__main">
        <div className="oss-request-card__left">
          <div className="oss-request-card__avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt={homeowner.full_name || ""} />
            ) : (
              <span>{initials || "HB"}</span>
            )}
          </div>

          <div className="oss-request-card__content">
            <h3>
              {homeowner.full_name || homeowner.email || t("common.homebuyer")}
              <span className="oss-request-card__id">#{item.id}</span>
            </h3>

            {withProperty ? (
              <p>
                <MapPin size={14} />
                <span>{formatAddress(property, t)}</span>
              </p>
            ) : (
              <div className="oss-request-card__prepurchase">
                <p className="oss-request-card__message">
                  <Home size={14} />
                  <span>{prePurchaseMessage}</span>
                </p>

                <div className="oss-request-card__prepurchase-grid">
                  <span>
                    <strong>
                      {t("requests.prePurchase.budget", "Budget")}
                    </strong>
                    {formatMoney(prePurchaseBudget)}
                  </span>

                  <span>
                    <strong>
                      {t("requests.prePurchase.region", "Region")}
                    </strong>
                    {formatPreferenceValue(prePurchaseRegion) ||
                      t("common.notAvailable")}
                  </span>

                  <span>
                    <strong>
                      {t("requests.prePurchase.timeline", "Timeline")}
                    </strong>
                    {formatPreferenceValue(prePurchaseTimeline) ||
                      t("common.notAvailable")}
                  </span>
                </div>
              </div>
            )}

            {withProperty ? (
              <div className="oss-request-card__meta">
                <span>
                  {formatDisplayLabel(property.property_type) ||
                    t("common.property")}
                </span>

                {property.surface_sqm ? (
                  <span>
                    {property.surface_sqm} {t("units.squareMeters")}
                  </span>
                ) : null}

                {property.epc_rating_current ? (
                  <span>
                    {t("property.epc")} {property.epc_rating_current}
                  </span>
                ) : null}

                {property.heating_type ? (
                  <span>{formatDisplayLabel(property.heating_type)}</span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="oss-request-card__right">
          <StatusPill status={item.status} t={t} />
        </div>
      </div>

      <div className="oss-request-card__actions">
        <button
          type="button"
          className="oss-request-card__accept"
          disabled={acceptingId === item.id || !isPending}
          onMouseEnter={() => setTooltip({ visible: false, x: 0, y: 0 })}
          onMouseMove={(e) => {
            e.stopPropagation();
            setTooltip({ visible: false, x: 0, y: 0 });
          }}
          onClick={(e) => {
            e.stopPropagation();
            onAcceptClick(item);
          }}
        >
          <CheckCircle2 size={15} />
          {acceptingId === item.id
            ? t("buttons.accepting")
            : t("buttons.accept")}
        </button>

        <button
          type="button"
          className="oss-request-card__reject"
          disabled={rejectingId === item.id || !isPending}
          onMouseEnter={() => setTooltip({ visible: false, x: 0, y: 0 })}
          onMouseMove={(e) => {
            e.stopPropagation();
            setTooltip({ visible: false, x: 0, y: 0 });
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRejectClick(item);
          }}
        >
          <XCircle size={15} />
          {rejectingId === item.id
            ? t("buttons.rejecting")
            : t("buttons.reject")}
        </button>

        <button
          type="button"
          className="oss-request-card__contact"
          disabled={
            contacting ||
            ["DECLINED", "CANCELLED", "WITHDRAWN"].includes(item.status)
          }
          onMouseEnter={() => setTooltip({ visible: false, x: 0, y: 0 })}
          onMouseMove={(e) => {
            e.stopPropagation();
            setTooltip({ visible: false, x: 0, y: 0 });
          }}
          onClick={async (e) => {
            e.stopPropagation();

            try {
              setContacting(true);

              const data = await contactOssConnectionRequest({
                accessToken,
                requestId: item.id,
              });

              navigate("/oss/communication", {
                state: {
                  threadId: data.thread?.id || data.id,
                  requestId: item.id,
                },
              });
            } catch (error) {
              console.error(error);
            } finally {
              setContacting(false);
            }
          }}
        >
          <Mail size={15} />
          {contacting ? t("buttons.openingChat") : t("buttons.contact")}
        </button>
      </div>

      {tooltip.visible
        ? createPortal(
            <span
              className="oss-request-card__floating-hint"
              style={{
                left: tooltip.x + 14,
                top: tooltip.y + 14,
              }}
            >
              {t("requests.clickForDetails")}
            </span>,
            document.body,
          )
        : null}
    </article>
  );
}

function AcceptRequestModal({
  requestToAccept,
  acceptingId,
  onCancel,
  onConfirm,
  t,
}) {
  if (!requestToAccept) return null;

  return createPortal(
    <div className="oss-reject-modal-backdrop">
      <div className="oss-reject-modal">
        <h3>{t("acceptModal.title")}</h3>

        <p>
          {t("acceptModal.description", {
            name:
              requestToAccept.homeowner?.full_name ||
              requestToAccept.homeowner?.email ||
              t("common.homebuyer"),
          })}
        </p>

        <div className="oss-reject-modal__actions">
          <button
            type="button"
            className="oss-reject-modal__secondary"
            onClick={onCancel}
            disabled={Boolean(acceptingId)}
          >
            {t("buttons.cancel")}
          </button>

          <button
            type="button"
            className="oss-reject-modal__accept"
            onClick={onConfirm}
            disabled={Boolean(acceptingId)}
          >
            {acceptingId ? t("buttons.accepting") : t("buttons.confirmAccept")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RejectRequestModal({
  requestToReject,
  rejectingId,
  onCancel,
  onConfirm,
  t,
}) {
  if (!requestToReject) return null;

  return createPortal(
    <div className="oss-reject-modal-backdrop">
      <div className="oss-reject-modal">
        <h3>{t("rejectModal.title")}</h3>

        <p>
          {t("rejectModal.description", {
            name:
              requestToReject.homeowner?.full_name ||
              requestToReject.homeowner?.email ||
              t("common.homebuyer"),
          })}
        </p>

        <div className="oss-reject-modal__actions">
          <button
            type="button"
            className="oss-reject-modal__secondary"
            onClick={onCancel}
            disabled={Boolean(rejectingId)}
          >
            {t("buttons.cancel")}
          </button>

          <button
            type="button"
            className="oss-reject-modal__danger"
            onClick={onConfirm}
            disabled={Boolean(rejectingId)}
          >
            {rejectingId ? t("buttons.rejecting") : t("buttons.confirmReject")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RequestSection({
  title,
  subtitle,
  requestType,
  withProperty = false,
  accessToken,
  postalCodes = EMPTY_ARRAY,
  selectedPostalCodes = EMPTY_ARRAY,
  onPostalCodesChange = () => {},
}) {
  const { t } = useTranslation("oss-requests");
  const navigate = useNavigate();
  const { showToast } = useToast();
  const pageSize = 3;

  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("PENDING");
  const [page, setPage] = React.useState(1);

  const [displayData, setDisplayData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [isSearching, setIsSearching] = React.useState(false);
  const [error, setError] = React.useState("");

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const [reloadKey, setReloadKey] = React.useState(0);

  const [acceptingId, setAcceptingId] = React.useState(null);
  const [requestToAccept, setRequestToAccept] = React.useState(null);

  const [rejectingId, setRejectingId] = React.useState(null);
  const [requestToReject, setRequestToReject] = React.useState(null);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 280);

    return () => window.clearTimeout(timer);
  }, [search]);

  const hasLoadedOnceRef = React.useRef(false);

  React.useEffect(() => {
    let active = true;

    async function loadRequests() {
      const isInitialLoad = !hasLoadedOnceRef.current;

      try {
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setIsSearching(true);
        }

        setError("");

        const allPostalCodesSelected =
          postalCodes.length > 0 &&
          selectedPostalCodes.length === postalCodes.length;

        const postalCodesForRequest =
          requestType === "RENOVATION" &&
          postalCodes.length > 0 &&
          !allPostalCodesSelected
            ? selectedPostalCodes
            : [];

        const result = await fetchOssConnectionRequests({
          accessToken,
          requestType,
          status: statusFilter,
          search: debouncedSearch,
          postalCodes: postalCodesForRequest,
          page,
          pageSize,
        });

        if (!active) return;

        setDisplayData(result);

        hasLoadedOnceRef.current = true;
      } catch (err) {
        if (!active) return;

        setError(err?.message || t("errors.loadFailed"));
      } finally {
        if (active) {
          setLoading(false);
          setIsSearching(false);
          setIsRefreshing(false);
        }
      }
    }

    if (accessToken) {
      loadRequests();
    }

    return () => {
      active = false;
    };
  }, [
    accessToken,
    requestType,
    statusFilter,
    debouncedSearch,
    page,
    postalCodes,
    selectedPostalCodes,
    reloadKey,
    t,
  ]);

  const requests = displayData?.results || [];
  const count = displayData?.count || 0;
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  function handleRefresh() {
    if (loading || isRefreshing || isSearching) {
      return;
    }

    setIsRefreshing(true);

    setReloadKey((previous) => previous + 1);
  }

  async function handleConfirmAccept() {
    if (!requestToAccept) return;

    const targetRequest = requestToAccept;

    try {
      setAcceptingId(targetRequest.id);

      const data = await acceptOssRequest({
        requestId: targetRequest.id,
        accessToken,
      });

      setRequestToAccept(null);

      showToast({
        type: "success",
        title: t("toast.acceptSuccessTitle", "Request accepted"),
        message:
          data?.detail ||
          t(
            "toast.acceptSuccessMessage",
            "The homebuyer request was accepted successfully.",
          ),
      });

      if (targetRequest.request_type === "RENOVATION") {
        navigate("/oss/projects", {
          replace: true,
          state: {
            acceptedRequestId: targetRequest.id,
            project: data?.project || null,
          },
        });

        return;
      }

      setDisplayData((prev) => ({
        ...prev,

        count: Math.max((prev?.count || 1) - 1, 0),

        results: (prev?.results || []).filter(
          (item) => item.id !== targetRequest.id,
        ),
      }));
    } catch (err) {
      console.error("Could not accept OSS request:", err);

      showToast({
        type: "error",
        title: t("toast.acceptErrorTitle", "Request not accepted"),
        message:
          err?.message ||
          t("errors.acceptFailed", "The request could not be accepted."),
      });
    } finally {
      setAcceptingId(null);
    }
  }

  async function handleConfirmReject() {
    if (!requestToReject) return;

    const targetRequest = requestToReject;

    try {
      setRejectingId(targetRequest.id);

      const data = await rejectOssRequest({
        requestId: targetRequest.id,
        accessToken,
        reason: "",
      });

      setDisplayData((prev) => ({
        ...prev,

        count: Math.max((prev?.count || 1) - 1, 0),

        results: (prev?.results || []).filter(
          (item) => item.id !== targetRequest.id,
        ),
      }));

      setRequestToReject(null);

      showToast({
        type: "success",
        title: t("toast.rejectSuccessTitle", "Request declined"),
        message:
          data?.detail ||
          t(
            "toast.rejectSuccessMessage",
            "The homebuyer request was declined successfully.",
          ),
      });
    } catch (err) {
      console.error("Could not decline OSS request:", err);

      showToast({
        type: "error",
        title: t("toast.rejectErrorTitle", "Request not declined"),
        message:
          err?.message ||
          t("errors.rejectFailed", "The request could not be declined."),
      });
    } finally {
      setRejectingId(null);
    }
  }

  return (
    <section className="oss-requests-section">
      <div className="oss-requests-section__header">
        <div>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>

        <span>{t("requests.count", { count })}</span>
      </div>

      <div className="oss-requests-toolbar">
        <div className="oss-requests-search">
          {isSearching ? (
            <LoaderCircle className="oss-requests-spin" size={15} />
          ) : (
            <Search size={16} />
          )}

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search.placeholder")}
          />
        </div>
        {requestType === "RENOVATION" ? (
          <PostalCodeFilter
            postalCodes={postalCodes}
            selectedPostalCodes={selectedPostalCodes}
            onChange={(codes) => {
              onPostalCodesChange?.(codes);
              setPage(1);
            }}
            t={t}
          />
        ) : null}

        <StatusTabs
          value={statusFilter}
          onChange={(nextStatus) => {
            setStatusFilter(nextStatus);
            setPage(1);
          }}
          t={t}
        />

        <button
          type="button"
          className="oss-requests-refresh"
          onClick={handleRefresh}
          disabled={loading || isRefreshing || isSearching}
          title={t("buttons.refresh", "Refresh requests")}
          aria-label={t("buttons.refresh", "Refresh requests")}
        >
          <RefreshCw
            size={17}
            strokeWidth={2.2}
            className={
              isRefreshing
                ? "oss-requests-refresh__icon oss-requests-refresh__icon--spinning"
                : "oss-requests-refresh__icon"
            }
          />
        </button>
      </div>

      {loading ? (
        <div className="oss-requests-loading">
          <LoaderCircle className="oss-requests-spin" size={22} />
          {t("loading")}
        </div>
      ) : error ? (
        <div className="oss-requests-error">{error}</div>
      ) : requests.length === 0 ? (
        <div className="oss-requests-empty">
          {t(`requests.emptyByStatus.${statusFilter}`, t("requests.empty"))}
        </div>
      ) : (
        <div className="oss-requests-list">
          {requests.map((item) => (
            <RequestCard
              key={item.id}
              item={item}
              withProperty={withProperty}
              t={t}
              accessToken={accessToken}
              acceptingId={acceptingId}
              rejectingId={rejectingId}
              onAcceptClick={setRequestToAccept}
              onRejectClick={setRequestToReject}
            />
          ))}
        </div>
      )}

      <Pagination
        count={count}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        t={t}
      />

      <AcceptRequestModal
        requestToAccept={requestToAccept}
        acceptingId={acceptingId}
        onCancel={() => setRequestToAccept(null)}
        onConfirm={handleConfirmAccept}
        t={t}
      />

      <RejectRequestModal
        requestToReject={requestToReject}
        rejectingId={rejectingId}
        onCancel={() => setRequestToReject(null)}
        onConfirm={handleConfirmReject}
        t={t}
      />
    </section>
  );
}

function PostalCodeFilter({
  postalCodes = [],
  selectedPostalCodes = [],
  onChange,
  t,
}) {
  const [open, setOpen] = React.useState(false);
  const filterRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!filterRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!postalCodes.length) {
    return null;
  }

  const allSelected = selectedPostalCodes.length === postalCodes.length;

  const summary = allSelected
    ? t("postalCodes.all", "All")
    : selectedPostalCodes.length === 1
      ? selectedPostalCodes[0]
      : t("postalCodes.selectedCount", "{{count}} selected", {
          count: selectedPostalCodes.length,
        });

  function selectAll() {
    onChange([...postalCodes]);
  }

  function togglePostalCode(code) {
    const isSelected = selectedPostalCodes.includes(code);

    if (isSelected) {
      // Keep at least one postal code selected. An empty array is reserved
      // for the backend meaning "no postal-code filter / all".
      if (selectedPostalCodes.length === 1) {
        return;
      }

      onChange(selectedPostalCodes.filter((item) => item !== code));
      return;
    }

    onChange([...selectedPostalCodes, code]);
  }

  return (
    <div className="oss-postal-filter" ref={filterRef}>
      <button
        type="button"
        className={`oss-postal-filter__trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <MapPin size={15} />

        <span className="oss-postal-filter__trigger-copy">
          <span className="oss-postal-filter__trigger-label">
            {t("postalCodes.label", "Postal codes")}
          </span>
          <strong>{summary}</strong>
        </span>

        <ChevronDown
          size={15}
          className={open ? "is-rotated" : ""}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div className="oss-postal-filter__menu">
          <div className="oss-postal-filter__menu-header">
            <div>
              <strong>{t("postalCodes.title", "Filter by postal code")}</strong>
              <span>
                {t(
                  "postalCodes.description",
                  "Show requests from the areas you want to manage.",
                )}
              </span>
            </div>

            <button
              type="button"
              className="oss-postal-filter__select-all"
              onClick={selectAll}
              disabled={allSelected}
            >
              {t("postalCodes.selectAll", "Select all")}
            </button>
          </div>

          <div className="oss-postal-filter__options">
            {postalCodes.map((code) => {
              const checked = selectedPostalCodes.includes(code);

              return (
                <label
                  className={`oss-postal-filter__option ${
                    checked ? "is-selected" : ""
                  }`}
                  key={code}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePostalCode(code)}
                  />

                  <span className="oss-postal-filter__checkbox" />
                  <span className="oss-postal-filter__code">{code}</span>
                </label>
              );
            })}
          </div>

          <p className="oss-postal-filter__hint">
            {t(
              "postalCodes.hint",
              "At least one postal code must remain selected.",
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default function OssRequestsPage() {
  const { t } = useTranslation("oss-requests");
  const { getAccessTokenSilently } = useAuth0();

  const [accessToken, setAccessToken] = React.useState("");
  const [tokenLoading, setTokenLoading] = React.useState(true);
  const [tokenError, setTokenError] = React.useState("");

  const [postalCodes, setPostalCodes] = React.useState([]);

  const [selectedPostalCodes, setSelectedPostalCodes] = React.useState([]);

  React.useEffect(() => {
    let active = true;

    async function loadToken() {
      try {
        setTokenLoading(true);
        setTokenError("");

        const token = await getAccessTokenSilently();

        if (active) setAccessToken(token);
      } catch {
        if (active) setTokenError(t("errors.authFailed"));
      } finally {
        if (active) setTokenLoading(false);
      }
    }

    loadToken();

    return () => {
      active = false;
    };
  }, [getAccessTokenSilently, t]);

  React.useEffect(() => {
    if (!accessToken) return;

    let active = true;

    async function loadPostalCodes() {
      try {
        const codes = await fetchOssCoveragePostalCodes({
          accessToken,
        });

        if (!active) return;

        setPostalCodes(codes);

        // Default = ALL selected.
        setSelectedPostalCodes(codes);
      } catch (error) {
        console.error("Failed to load OSS postal codes:", error);
      }
    }

    loadPostalCodes();

    return () => {
      active = false;
    };
  }, [accessToken]);

  if (tokenLoading) {
    return (
      <div className="oss-requests-page">
        <div className="oss-requests-loading oss-requests-loading--page">
          <LoaderCircle className="oss-requests-spin" size={26} />
          {t("loading")}
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="oss-requests-page">
        <div className="oss-requests-error">{tokenError}</div>
      </div>
    );
  }

  return (
    <div className="oss-requests-page">
      <div className="oss-requests-hero">
        <h1>{t("title")}</h1>
        <p>{t("subtitle")}</p>
      </div>

      <RequestSection
        title={t("sections.withProperty.title")}
        subtitle={t("sections.withProperty.subtitle")}
        requestType="RENOVATION"
        withProperty
        accessToken={accessToken}
        postalCodes={postalCodes}
        selectedPostalCodes={selectedPostalCodes}
        onPostalCodesChange={setSelectedPostalCodes}
      />

      <RequestSection
        title={t("sections.lookingForProperty.title")}
        subtitle={t("sections.lookingForProperty.subtitle")}
        requestType="PRE_PURCHASE"
        accessToken={accessToken}
      />
    </div>
  );
}
